import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import InputIcon from '@mui/icons-material/InputTwoTone';
import { IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '../../react-auth0-spa';
import { isPathnameValid } from '../../utils';
import TopBarRightButtons from './TopBarRightButtons';
import TopBarBrand from './TopBarBrand';
import { useSignOut } from '../../hooks/useSignOut';
import HelpButton from '../../pages/help/HelpButton';
import GlobalSearchTrigger from '../../components/globalSearch/GlobalSearchTrigger';

interface TopBarProps {
    sideBarOpen: boolean;
    onOpenSideBar: () => void;
    onCloseSideBar: () => void;
}

const TopBar = (props: TopBarProps) => {
    const { isAuthenticated, user } = useAuth0();
    const signOut = useSignOut();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const pathName = location.pathname;
    const isInMasterMode = pathName.startsWith('/master');
    // Set when the user drilled into a detail page from the all-tenant
    // view. Carries the exact all-tenant URL (with filters) so back
    // navigation restores the view they came from instead of a default.
    const navState = location.state as { fromMaster?: string } | null;
    const fromMaster = navState?.fromMaster ?? null;

    function handleLogout() {
        signOut();
    }

    function handleGoBack() {
        // The detail page was reached by a single push from the all-tenant
        // view (that's exactly when `fromMaster` is set), so a history POP
        // returns to its precise URL — restoring both the filters (encoded
        // in the query string) and the scroll position. Fall back to
        // navigating the stored URL directly if there's no history entry.
        if (fromMaster && window.history.length > 1) {
            navigate(-1);
            return;
        }
        if (fromMaster) {
            navigate(fromMaster);
            return;
        }
        navigate(-1);
    }

    return (
        <section
            className={`fixed inset-x-0 flex flex-row items-center justify-between
                     h-16 lg:px-6 px-2 bg-text-contrast shadow-md z-40`}
        >
            {fromMaster && (
                <Tooltip title={t('master.back_to_overview')}>
                    <IconButton
                        onClick={handleGoBack}
                        className={'hidden lg:inline-flex text-foreground mr-1'}
                        size={'small'}
                        aria-label={t('master.back_to_overview')}
                    >
                        <ArrowBackIosIcon fontSize={'small'} className={'text-foreground'} />
                    </IconButton>
                </Tooltip>
            )}
            <TopBarBrand variant={'desktop'} isInMasterMode={isInMasterMode} />
            {/* Desktop spacer only: on mobile the row below takes the free space,
                and a growing spacer would split it and pull the help button
                towards the middle. */}
            <div className={'lg:grow'} />
            {isAuthenticated && <GlobalSearchTrigger />}
            {isAuthenticated && <HelpButton />}
            {isAuthenticated && (
                <div className={'hidden lg:flex flex-row items-center'}>
                    <Tooltip title={user.email}>
                        <img
                            src={user.picture}
                            className={'rounded-2xl size-7'}
                            alt={'Avatar'}
                        />
                    </Tooltip>
                    <Tooltip title={'Logout'}>
                        <IconButton
                            onClick={handleLogout}
                            className={'text-foreground'}
                            size={'large'}
                        >
                            <InputIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            )}
            {/* The mobile row takes exactly the space the help button leaves and
                may shrink below its content (`min-w-0`); the logo gives way
                first and the menu button never does. As `w-full` it relied on
                shrinking from 100% to fit, and on iPhones it ran past the screen
                edge, pushing the menu button off it and leaving no way into the
                navigation. Sized like this, the menu's position no longer
                depends on how a browser measures the logo. */}
            <div
                className={'flex lg:hidden flex-1 min-w-0 justify-between items-center'}
            >
                <div className={'min-w-12 shrink-0'}>
                    {isPathnameValid(pathName) && (
                        <IconButton onClick={handleGoBack}>
                            <ArrowBackIosIcon className={'text-foreground'} />
                        </IconButton>
                    )}
                </div>

                <TopBarBrand variant={'mobile'} isInMasterMode={isInMasterMode} />
                <div className={'shrink-0'}>
                    <TopBarRightButtons
                        {...props}
                        iconClassName={'text-foreground'}
                    />
                </div>
            </div>
        </section>
    );
};

export default TopBar;
